/**
 * History Dropdown — open/close, height calculation, touch handling, selection state
 * Shared mutable state for dropdown + keyboard modules lives here.
 * # ⛓️ CURSOR_ANCHOR:search:history-dropdown
 */
(function() {
  // Shared mutable state accessible by keyboard.js via window._historyDropdown.state
  var state = {
    selectedIndex: -1,
    lastSelectedTerm: null,
    lastSelectedIndex: -1,
    lastSelectedHistoryId: null,
    dropdown: null,
    searchInput: null,
    historyButton: null
  };

  /** Bind DOM elements to shared state. Called once from initSearchHistory. */
  function initDropdownState(dropdownEl, searchInputEl, historyButtonEl) {
    state.dropdown = dropdownEl;
    state.searchInput = searchInputEl;
    state.historyButton = historyButtonEl;
    state.selectedIndex = -1;
    state.lastSelectedTerm = null;
    state.lastSelectedIndex = -1;
    state.lastSelectedHistoryId = null;
  }

  /** Remove .selected from all items, reset selectedIndex. */
  function resetHistorySelection() {
    state.selectedIndex = -1;
    if (state.dropdown) {
      var items = state.dropdown.querySelectorAll('.search-history-item');
      items.forEach(function(item) {
        item.classList.remove('selected');
      });
    }
  }

  /** Restore highlight on the last-interacted item (by id > index > term). */
  function restoreLastSelection() {
    if (!state.dropdown) return;

    var visibleItems = Array.from(state.dropdown.querySelectorAll('.search-history-item')).filter(function(item) {
      if (item.classList.contains('branch-hidden')) return false;
      return item.style.display !== 'none';
    });

    // Priority: stable id -> index -> term (legacy fallback).
    var targetIndex = -1;
    if (state.lastSelectedHistoryId) {
      for (var iId = 0; iId < visibleItems.length; iId++) {
        if (visibleItems[iId].getAttribute('data-id') === state.lastSelectedHistoryId) {
          targetIndex = iId;
          break;
        }
      }
    }
    if (targetIndex === -1 && state.lastSelectedIndex !== -1 && state.lastSelectedIndex < visibleItems.length) {
      targetIndex = state.lastSelectedIndex;
    }
    if (targetIndex === -1 && state.lastSelectedTerm) {
      for (var i = 0; i < visibleItems.length; i++) {
        if (visibleItems[i].getAttribute('data-term') === state.lastSelectedTerm) {
          targetIndex = i;
          break;
        }
      }
    }

    if (targetIndex !== -1) {
      state.selectedIndex = targetIndex;
      visibleItems[targetIndex].classList.add('selected');
      visibleItems[targetIndex].scrollIntoView({ block: 'nearest', behavior: 'instant' });
    }
  }

  /** Calculate and set dropdown height for mobile viewport. Reset on desktop. */
  function updateDropdownHeight() {
    var dropdown = state.dropdown;
    if (!dropdown || !dropdown.classList.contains('active')) return;

    if (window.innerWidth <= 768) {
      var searchForm = state.historyButton
        ? state.historyButton.closest('.search-form-container, .search-form')
        : null;
      if (searchForm) {
        var rect = searchForm.getBoundingClientRect();
        var dropdownTop = rect.bottom;
        var availableHeight = window.innerHeight - dropdownTop;

        dropdown.style.top = dropdownTop + 'px';
        dropdown.style.bottom = '0px';
        dropdown.style.height = availableHeight + 'px';

        var dropdownHeader = dropdown.querySelector('.search-history-dropdown-header');
        var dropdownList = dropdown.querySelector('.search-history-dropdown-list');
        if (dropdownHeader && dropdownList) {
          var headerHeight = dropdownHeader.offsetHeight;
          dropdownList.style.maxHeight = (availableHeight - headerHeight) + 'px';
        }
      }
    } else {
      dropdown.style.top = '';
      dropdown.style.bottom = '';
      dropdown.style.height = '';
      dropdown.style.width = '';
      dropdown.style.left = '';
      dropdown.style.right = '';
      var dropdownList = dropdown.querySelector('.search-history-dropdown-list');
      if (dropdownList) {
        dropdownList.style.maxHeight = '';
      }
    }
  }

  function lockMobileScroll() {
    if (window.innerWidth <= 768) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
    }
  }

  function unlockMobileScroll() {
    if (window.innerWidth <= 768) {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
    }
  }

  /** Close dropdown, reset selection, clear confirm states, unlock mobile scroll. */
  function closeHistoryDropdown() {
    var dropdown = state.dropdown;
    var wasOpen = dropdown && dropdown.classList.contains('active');
    if (wasOpen) {
      dropdown.classList.remove('active');
    }
    if (state.historyButton) {
      state.historyButton.classList.remove('is-open');
      state.historyButton.setAttribute('aria-expanded', 'false');
    }
    if (wasOpen) {
      resetHistorySelection();
      dropdown.querySelectorAll('.search-history-delete.confirm').forEach(function(btn) {
        btn.classList.remove('confirm');
      });
      unlockMobileScroll();
    }
  }

  /** Open dropdown: render history, highlight active item or restore last selection. */
  function openHistoryDropdown() {
    var dropdown = state.dropdown;
    if (!dropdown || dropdown.classList.contains('active')) return;

    resetHistorySelection();
    if (typeof renderHistory === 'function') {
      renderHistory();
    } else if (window.renderHistory) {
      window.renderHistory();
    }
    dropdown.classList.add('active');
    if (state.historyButton) {
      state.historyButton.classList.add('is-open');
      state.historyButton.setAttribute('aria-expanded', 'true');
    }

    // If there's an active record (CURRENT_HISTORY_ID), highlight it
    var activeItemSelected = false;
    if (window.CURRENT_HISTORY_ID) {
      var activeEl = dropdown.querySelector('.search-history-item.is-active');
      if (activeEl) {
        resetHistorySelection();
        activeEl.classList.add('selected');
        var allVisible = Array.from(dropdown.querySelectorAll('.search-history-item')).filter(function(it) {
          if (it.classList.contains('branch-hidden')) return false;
          return it.style.display !== 'none';
        });
        state.selectedIndex = allVisible.indexOf(activeEl);
        state.lastSelectedHistoryId = window.CURRENT_HISTORY_ID;
        activeItemSelected = true;
        requestAnimationFrame(function() {
          activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        });
      }
    }
    if (!activeItemSelected) {
      restoreLastSelection();
    }

    lockMobileScroll();
    requestAnimationFrame(function() {
      requestAnimationFrame(function() {
        updateDropdownHeight();
      });
    });
  }

  /** Attach touch event handlers to prevent background scroll on mobile. */
  function setupTouchHandlers() {
    var dropdown = state.dropdown;
    if (!dropdown) return;

    var dropdownHeader = dropdown.querySelector('.search-history-dropdown-header');
    var dropdownList = dropdown.querySelector('.search-history-dropdown-list');

    if (dropdownHeader) {
      dropdownHeader.addEventListener('touchstart', function(e) {
        e.stopPropagation();
      }, { passive: false });

      dropdownHeader.addEventListener('touchmove', function(e) {
        e.stopPropagation();
        e.preventDefault();
      }, { passive: false });
    }

    dropdown.addEventListener('touchstart', function(e) {
      if (e.target === dropdown || e.target === dropdownHeader) {
        e.stopPropagation();
      }
    }, { passive: true });

    dropdown.addEventListener('touchmove', function(e) {
      if (e.target === dropdown || e.target === dropdownHeader || !dropdownList.contains(e.target)) {
        e.stopPropagation();
      }
    }, { passive: false });

    window.addEventListener('resize', updateDropdownHeight);
    window.addEventListener('orientationchange', function() {
      setTimeout(updateDropdownHeight, 100);
    });
  }

  // Module export
  window._historyDropdown = {
    state: state,
    initDropdownState: initDropdownState,
    resetHistorySelection: resetHistorySelection,
    restoreLastSelection: restoreLastSelection,
    updateDropdownHeight: updateDropdownHeight,
    closeHistoryDropdown: closeHistoryDropdown,
    openHistoryDropdown: openHistoryDropdown,
    lockMobileScroll: lockMobileScroll,
    unlockMobileScroll: unlockMobileScroll,
    setupTouchHandlers: setupTouchHandlers
  };

  // Backward-compatible global exports
  window.updateDropdownHeight = updateDropdownHeight;
  window.closeHistoryDropdown = closeHistoryDropdown;
  window.openHistoryDropdown = openHistoryDropdown;
})();
